import { useEffect, useState } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { PRESALE_VISIBLE } from '@/config/stripe';

/**
 * Who may reach the presale checkout.
 *
 * - PRESALE_VISIBLE true: everyone who is logged in (the launch state).
 * - Otherwise: admins (is_admin) and named test accounts (presale_tester).
 *   Both columns are protected by the protect_profile_columns trigger, so a
 *   user cannot grant themselves access through the profile update policy.
 *
 * `checking` is true until the answer is known; render nothing decisive before then.
 */
export function usePresaleAccess(): { canCheckout: boolean; checking: boolean } {
  const { user, isFounder, founderChecked, loading } = useAuth();
  const userId = user?.id ?? null;
  const [tester, setTester] = useState<boolean | null>(null);

  useEffect(() => {
    if (!userId) {
      setTester(false);
      return;
    }
    let cancelled = false;
    supabase
      .from('profiles')
      .select('presale_tester')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setTester(data?.presale_tester === true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const checking = loading || (!!userId && (!founderChecked || tester === null));
  const canCheckout = PRESALE_VISIBLE || isFounder || tester === true;

  return { canCheckout, checking };
}
